/**
 * Training Catalog Service - Kategorie i typy szkoleń (słowniki)
 * wojskaspecjalne.pl
 */

import { supabase } from './supabase';
import type { Database } from '../types/database.types';

export type TrainingCategoryRow = Database['public']['Tables']['training_categories']['Row'];
export type TrainingTypeRow = Database['public']['Tables']['training_types']['Row'];

export type TrainingCategory = TrainingCategoryRow;
export type TrainingType = TrainingTypeRow & { category?: TrainingCategory | null };

export interface CreateTrainingCategoryData {
  name: string;
  slug?: string | null;
  description?: string | null;
  is_active?: boolean;
}

export interface UpdateTrainingCategoryData extends Partial<CreateTrainingCategoryData> {
  id: string;
}

export interface CreateTrainingTypeData {
  category_id?: string | null;
  name: string;
  slug?: string | null;
  description?: string | null;
  is_active?: boolean;
}

export interface UpdateTrainingTypeData extends Partial<CreateTrainingTypeData> {
  id: string;
}

export async function getAllTrainingCategories(options?: {
  includeInactive?: boolean;
}): Promise<{ success: boolean; data?: TrainingCategory[]; error?: string }> {
  try {
    let query = supabase
      .from('training_categories')
      .select('*')
      .order('name', { ascending: true });

    if (!options?.includeInactive) {
      query = query.eq('is_active', true);
    }

    const { data, error } = await query;
    if (error) throw error;

    return { success: true, data: data ?? [] };
  } catch (error: any) {
    console.error('Error fetching training categories:', error);
    return { success: false, error: error.message };
  }
}

export async function createTrainingCategory(
  payload: CreateTrainingCategoryData
): Promise<{ success: boolean; data?: TrainingCategory; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('training_categories')
      .insert({
        name: payload.name.trim(),
        slug: payload.slug?.trim() || null,
        description: payload.description?.trim() || null,
        is_active: payload.is_active ?? true,
      })
      .select('*')
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error: any) {
    console.error('Error creating training category:', error);
    return { success: false, error: error.message };
  }
}

export async function updateTrainingCategory(
  payload: UpdateTrainingCategoryData
): Promise<{ success: boolean; data?: TrainingCategory; error?: string }> {
  try {
    const { id, ...fields } = payload;

    const updateFields: Partial<TrainingCategoryRow> = {};
    if (fields.name !== undefined) updateFields.name = fields.name.trim();
    if (fields.slug !== undefined) updateFields.slug = fields.slug?.trim() || null;
    if (fields.description !== undefined) updateFields.description = fields.description?.trim() || null;
    if (fields.is_active !== undefined) updateFields.is_active = fields.is_active;

    const { data, error } = await supabase
      .from('training_categories')
      .update(updateFields)
      .eq('id', id)
      .select('*')
      .single();

    if (error) throw error;
    return { success: true, data };
  } catch (error: any) {
    console.error('Error updating training category:', error);
    return { success: false, error: error.message };
  }
}

export async function getAllTrainingTypes(options?: {
  includeInactive?: boolean;
  categoryId?: string;
}): Promise<{ success: boolean; data?: TrainingType[]; error?: string }> {
  try {
    let query = supabase
      .from('training_types')
      .select(`
        *,
        category:training_categories (*)
      `)
      .order('name', { ascending: true });

    if (!options?.includeInactive) {
      query = query.eq('is_active', true);
    }

    if (options?.categoryId) {
      query = query.eq('category_id', options.categoryId);
    }

    const { data, error } = await query;
    if (error) throw error;

    return { success: true, data: (data ?? []) as any };
  } catch (error: any) {
    console.error('Error fetching training types:', error);
    return { success: false, error: error.message };
  }
}

export async function createTrainingType(
  payload: CreateTrainingTypeData
): Promise<{ success: boolean; data?: TrainingType; error?: string }> {
  try {
    const { data, error } = await supabase
      .from('training_types')
      .insert({
        category_id: payload.category_id ?? null,
        name: payload.name.trim(),
        slug: payload.slug?.trim() || null,
        description: payload.description?.trim() || null,
        is_active: payload.is_active ?? true,
      })
      .select(`
        *,
        category:training_categories (*)
      `)
      .single();

    if (error) throw error;
    return { success: true, data: data as any };
  } catch (error: any) {
    console.error('Error creating training type:', error);
    return { success: false, error: error.message };
  }
}

export async function updateTrainingType(
  payload: UpdateTrainingTypeData
): Promise<{ success: boolean; data?: TrainingType; error?: string }> {
  try {
    const { id, ...fields } = payload;

    const updateFields: Partial<TrainingTypeRow> = {};
    if (fields.category_id !== undefined) updateFields.category_id = fields.category_id;
    if (fields.name !== undefined) updateFields.name = fields.name.trim();
    if (fields.slug !== undefined) updateFields.slug = fields.slug?.trim() || null;
    if (fields.description !== undefined) updateFields.description = fields.description?.trim() || null;
    if (fields.is_active !== undefined) updateFields.is_active = fields.is_active;

    const { data, error } = await supabase
      .from('training_types')
      .update(updateFields)
      .eq('id', id)
      .select(`
        *,
        category:training_categories (*)
      `)
      .single();

    if (error) throw error;
    return { success: true, data: data as any };
  } catch (error: any) {
    console.error('Error updating training type:', error);
    return { success: false, error: error.message };
  }
}


